import { a3Copy } from "@/components/assignments/a3-copy";
import type { Navigator } from "react-router";

/** One mounted leave guard: what it would lose, and how it asks. */
export type LeaveGuard = {
  /** Names this guard's unsaved work inside another guard's prompt. */
  label: string;
  /** `busy`: a write is in flight, so leaving waits; its `ask` explains and never proceeds. */
  state: () => "clean" | "unsaved" | "busy";
  /** Asks once. `also` names the other unsaved work the answer covers. Consent
   * calls `proceed`; refusal calls nothing. */
  ask: (also: string[], proceed: () => void) => void;
  /** Its prompt can save before leaving, so it asks for everyone. */
  saves?: boolean;
};

type Session = {
  guards: LeaveGuard[];
  index: number;
  restoring: number | null;
  approvedIndex: number | null;
  /** A departure already answered by `leaveOnce`: its steps don't ask again. */
  answered: boolean;
  restore: () => void;
};

let installed = false;
const sessions = new Map<Navigator, Session>();

/** The single owner of leaving a page with unsaved work. Guards register;
 * this wraps the router's navigator once and owns the POP bookkeeping, so
 * one navigation asks one question that names everything it would lose, and
 * mount or unmount order cannot corrupt the wrap.
 *
 * BrowserRouter subscribes during construction, so this listener is installed
 * by the application bootstrap before React renders the router. PUSH/REPLACE
 * are refused before navigation. POP is restored before asking, then replayed
 * once on consent. React never observes a refused location.
 */
export function installAssignmentDraftPopInterceptor() {
  if (installed) return;
  window.addEventListener("popstate", event => {
    for (const session of [...sessions.values()].reverse()) {
      pop(session, event);
      if (event.cancelBubble) return;
    }
  }, true);
  installed = true;
}

/** `Leaving also discards a and b.` for the prompt that speaks for other guards. */
export function alsoDiscards(also: string[]) {
  const named = also.length > 1 ? a3Copy.and(also.slice(0, -1).join(", "), also[also.length - 1]) : also[0];
  return a3Copy.leavingAlsoDiscards(named);
}

function decide(session: Session, proceed: () => void) {
  const guards = [...session.guards].reverse();
  const busy = guards.find(guard => guard.state() === "busy");
  if (busy) { busy.ask([], proceed); return; }
  const unsaved = guards.filter(guard => guard.state() === "unsaved");
  if (!unsaved.length) { proceed(); return; }
  const asker = unsaved.find(guard => guard.saves) ?? unsaved[0];
  asker.ask(unsaved.filter(guard => guard !== asker).map(guard => guard.label), proceed);
}

/**
 * One departure made of several router steps, such as recording where the
 * person was and then going, asks one question. Consent runs every step with
 * that answer; refusal runs none.
 */
export function leaveOnce(navigator: Navigator, steps: () => void) {
  const session = sessions.get(navigator);
  if (!session) { steps(); return; }
  decide(session, () => {
    session.answered = true;
    try { steps(); } finally { session.answered = false; }
  });
}

function pop(session: Session, event: PopStateEvent) {
  const next = event.state?.idx;
  if (session.restoring !== null) {
    event.stopImmediatePropagation();
    if (typeof next === "number" && next !== session.index) {
      window.history.go(session.index - next);
      return;
    }
    const delta = session.restoring;
    session.restoring = null;
    decide(session, () => { session.approvedIndex = session.index + delta; window.history.go(delta); });
    return;
  }
  const approved = session.approvedIndex !== null && next === session.approvedIndex;
  session.approvedIndex = null;
  if (approved || session.guards.every(guard => guard.state() === "clean")) {
    if (typeof next === "number") session.index = next;
    return;
  }
  const delta = typeof next === "number" ? next - session.index : 0;
  if (delta !== 0) {
    event.stopImmediatePropagation();
    session.restoring = delta;
    window.history.go(-delta);
  }
}

export function registerLeaveGuard(navigator: Navigator, guard: LeaveGuard) {
  let session = sessions.get(navigator);
  if (!session) {
    const { push, replace, go } = navigator;
    const created: Session = {
      guards: [],
      index: window.history.state?.idx ?? 0,
      restoring: null,
      approvedIndex: null,
      answered: false,
      restore: () => { navigator.push = push; navigator.replace = replace; navigator.go = go; },
    };
    const asking = (proceed: () => void) => created.answered ? proceed() : decide(created, proceed);
    navigator.push = (...args) => asking(() => {
      created.approvedIndex = null;
      push.apply(navigator, args);
      created.index = window.history.state?.idx ?? created.index;
    });
    navigator.replace = (...args) => asking(() => { created.approvedIndex = null; replace.apply(navigator, args); });
    // MemoryRouter and programmatic movement share the same refusal. Native
    // browser movement is handled by the capture listener above.
    navigator.go = delta => asking(() => { created.approvedIndex = created.index + delta; go.call(navigator, delta); });
    sessions.set(navigator, created);
    session = created;
  }
  const owner = session;
  owner.guards.push(guard);
  return () => {
    const index = owner.guards.indexOf(guard);
    if (index !== -1) owner.guards.splice(index, 1);
    if (!owner.guards.length && sessions.get(navigator) === owner) {
      owner.restore();
      sessions.delete(navigator);
    }
  };
}
